import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from '@/shared/api/trpc';
import { messagesTabListFilter } from '../model/cards';

const SIXTY_SECONDS = 60 * 1000;

/**
 * Card totals for the Messages tab — same API + filter shape as
 * `ExternalMessagesTable` (`externalMessages.findAll` → `countAll`), so the
 * number on each card matches the list when that card is selected (XT380-023
 * exact-count invariant for the messages entity).
 *
 * Deliberately does **not** use `operations.countMany` (date-windowed ops
 * engine) — that path diverged from the messages table after cards stopped
 * drilling into operation items.
 *
 * **N round-trips (known trade-off):** one `findAll` per card via
 * `trpc.useQueries`. A future `externalMessages.countMany` would match the
 * ops-tab batch path; out of scope for the XT-380 follow-up.
 */
export const useMessagesFilterCardCounts = (cardIds: readonly string[]) => {
  const results = trpc.useQueries((t) =>
    cardIds.map((id) => {
      const filter = messagesTabListFilter(id);
      return t.externalMessages.findAll(
        {
          offset: 0,
          limit: 1,
          ...(filter.status ? { status: filter.status } : {}),
          ...(filter.maxChars ? { maxChars: true } : {}),
          ...(filter.operationStatusCodes?.length
            ? { operationStatusCodes: filter.operationStatusCodes }
            : {}),
        },
        {
          enabled: cardIds.length > 0,
          staleTime: SIXTY_SECONDS,
          refetchInterval: SIXTY_SECONDS,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
          placeholderData: keepPreviousData,
          trpc: { context: { skipBatch: true } },
        }
      );
    })
  );

  // Per-card totals: loading / error → undefined (skeleton), never collapse the
  // whole row to zeros when a single query fails.
  const totals: (number | undefined)[] = results.map((r) => {
    if (r.isError) return undefined;
    if (r.data === undefined) return undefined;
    return r.data.countAll;
  });

  return {
    totals,
    isLoading: results.some((r) => r.isLoading),
  };
};
